import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/customer-tasks';

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'OPEN', label: 'Açık' },
  { key: 'DONE', label: 'Tamamlanan' },
  { key: 'CANCELLED', label: 'İptal' },
];

const STATUS_META = {
  OPEN: { label: 'Açık', tone: 'warn' },
  DONE: { label: 'Tamamlandı', tone: 'ok' },
  CANCELLED: { label: 'İptal', tone: 'neutral' },
};

const PAGE_SIZE_DEFAULT = 20;

const EMPTY_FORM = {
  assigneeRepresentativeId: '',
  assigneeName: '',
  customerId: '',
  customerTitle: '',
  title: '',
  description: '',
  assignedByName: '',
  status: 'OPEN',
  completedAt: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    openCount: pick(data, 'openCount', 'OpenCount') ?? 0,
    doneCount: pick(data, 'doneCount', 'DoneCount') ?? 0,
    cancelledCount: pick(data, 'cancelledCount', 'CancelledCount') ?? 0,
  };
}

function statusPillFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function badge(label, value, tone) {
  const tones = {
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function CustomerTasksPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ openCount: 0, doneCount: 0, cancelledCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        openCount: data.openCount,
        doneCount: data.doneCount,
        cancelledCount: data.cancelledCount,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing({ mode: 'create' }); setForm(EMPTY_FORM); };

  const openEdit = (row) => {
    setEditing({ mode: 'edit', id: pick(row, 'id', 'Id') });
    const completedAtIso = pick(row, 'completedAt', 'CompletedAt');
    setForm({
      assigneeRepresentativeId: pick(row, 'assigneeRepresentativeId', 'AssigneeRepresentativeId') || '',
      assigneeName: pick(row, 'assigneeName', 'AssigneeName') || '',
      customerId: pick(row, 'customerId', 'CustomerId') || '',
      customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
      title: pick(row, 'title', 'Title') || '',
      description: pick(row, 'description', 'Description') || '',
      assignedByName: pick(row, 'assignedByName', 'AssignedByName') || '',
      status: pick(row, 'status', 'Status') || 'OPEN',
      completedAt: completedAtIso ? completedAtIso.slice(0, 16) : '',
    });
  };

  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submitForm = async () => {
    if (!form.assigneeRepresentativeId.trim()) { showToast('error', 'Temsilci ID zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }

    setSaving(true);
    try {
      const body = {
        assigneeRepresentativeId: form.assigneeRepresentativeId.trim(),
        customerId: form.customerId.trim() || null,
        title: form.title.trim(),
        description: form.description.trim() || null,
        assignedByName: form.assignedByName.trim() || null,
        status: form.status,
        completedAt: form.completedAt ? new Date(form.completedAt).toISOString() : null,
      };
      const url = editing.mode === 'edit' ? `${API_BASE}/${editing.id}` : API_BASE;
      const method = editing.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', editing.mode === 'edit' ? 'Görev güncellendi.' : 'Görev oluşturuldu.');
      closeEdit();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    const id = pick(deleteRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Görev silindi.');
      setDeleteRow(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'title', 'Title'),
        pick(row, 'description', 'Description'),
        pick(row, 'assigneeName', 'AssigneeName'),
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'assignedByName', 'AssignedByName'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'AssigneeName': return pick(row, 'assigneeName', 'AssigneeName') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'title',
      headerName: 'Görev',
      flex: 1.4,
      minWidth: 220,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700 }}>{pick(row, 'title', 'Title') || '-'}</span>
          {pick(row, 'description', 'Description') && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              {String(pick(row, 'description', 'Description')).slice(0, 80)}
              {String(pick(row, 'description', 'Description')).length > 80 ? '…' : ''}
            </span>
          )}
        </div>
      ),
    },
    {
      field: 'assigneeName',
      headerName: 'Atanan',
      flex: 1,
      minWidth: 160,
      valueGetter: ({ row }) => pick(row, 'assigneeName', 'AssigneeName') || '-',
    },
    {
      field: 'customerTitle',
      headerName: 'Müşteri',
      flex: 1.1,
      minWidth: 170,
      valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '(Genel)',
    },
    {
      field: 'assignedByName',
      headerName: 'Veren',
      flex: 0.8,
      minWidth: 130,
      valueGetter: ({ row }) => pick(row, 'assignedByName', 'AssignedByName') || '-',
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.6,
      minWidth: 130,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'completedAt',
      headerName: 'Tamam.',
      flex: 0.8,
      minWidth: 130,
      valueGetter: ({ row }) => formatDate(pick(row, 'completedAt', 'CompletedAt')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.9,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={() => openEdit(row)}
          >
            Düzenle
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#dc2626' }}
            onClick={() => setDeleteRow(row)}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Saha Görevleri</h1>
          <p style={s.pageSubtitle}>
            Ofisten saha temsilcisine atanan görevler. Mobile çift yönlü sync ile çeker;
            temsilci tamamlandı/iptal işaretler. Müşteri opsiyonel (genel görev de olabilir).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Açık', counts.openCount, 'orange')}
          {badge('Tamamlanan', counts.doneCount, 'green')}
          {badge('İptal', counts.cancelledCount, 'gray')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
          <button type="button" style={s.btnPrimary} onClick={openCreate}>
            + Yeni Görev
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Görev / atanan / müşteri / veren..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openEdit}
        exportFileName="saha-gorevleri"
      />

      {editing && (
        <div style={s.overlay} onClick={closeEdit}>
          <div style={{ ...s.modal, maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editing.mode === 'edit' ? 'Görevi Düzenle' : 'Yeni Görev'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeEdit}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Atanan Temsilci ID (GUID) *</label>
                <input
                  type="text"
                  value={form.assigneeRepresentativeId}
                  onChange={(e) => setForm({ ...form, assigneeRepresentativeId: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  style={{ ...s.input, fontFamily: 'monospace', fontSize: 12 }}
                />
                {form.assigneeName && (
                  <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>
                    ✓ {form.assigneeName}
                  </div>
                )}
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Müşteri ID (GUID — opsiyonel)</label>
                <input
                  type="text"
                  value={form.customerId}
                  onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                  placeholder="(genel görev için boş bırakın)"
                  style={{ ...s.input, fontFamily: 'monospace', fontSize: 12 }}
                />
                {form.customerTitle && (
                  <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>
                    ✓ {form.customerTitle}
                  </div>
                )}
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Başlık *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  style={s.input}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Açıklama</label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  style={s.textarea}
                />
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Görevi Veren</label>
                  <input
                    type="text"
                    value={form.assignedByName}
                    onChange={(e) => setForm({ ...form, assignedByName: e.target.value })}
                    placeholder="örn. Bölge Müdürü"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    style={s.select}
                  >
                    {STATUS_TABS.filter(t => t.key).map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Tamamlanma</label>
                  <input
                    type="datetime-local"
                    value={form.completedAt}
                    onChange={(e) => setForm({ ...form, completedAt: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeEdit} disabled={saving}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={submitForm}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : (editing.mode === 'edit' ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRow && (
        <div style={s.overlay} onClick={() => setDeleteRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Görevi Sil</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 14, color: '#1f2937' }}>
                <strong>{pick(deleteRow, 'title', 'Title')}</strong> görevini silmek istediğinize emin misiniz?
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CustomerTasksPageLegacy;
